/**
 * macOS unified title bar — leading inset for the top bar.
 *
 * When the macOS window uses a full-size content view, our content runs the whole height of the
 * window and AppKit's traffic lights float ON TOP of it, over the top-left of whatever we draw.
 * Unity appends the measured inset so the top bar can pad its leading edge clear of them.
 *
 * The value is measured natively (AppKit lays the buttons out, and Tahoe may space them
 * differently than earlier releases) rather than hardcoded on this side. It arrives in points,
 * which the webview treats as CSS px at the same scale, so it needs no conversion.
 *
 * Absent in every other mode — iOS, Windows, standalone, online review, and the Editor — where
 * nothing overlaps the bar and the custom property stays unset, so the CSS falls back to 0px and
 * the bar is byte-identical to before.
 */

/** Query param Unity appends on macOS embedded routes. Mirrors the Unity-side twin helpers
 *  (PurchasingWebViewPopupPanel / TimelineWebViewPanel .GetTitleBarInsetQueryFragment). */
export const TITLEBAR_INSET_LEADING_PARAM = 'titlebarInsetLeading';

/** Custom property the top bars read. Set on the root element so both the projects bar and the
 *  board bar pick it up without either needing to know where it came from. */
export const TITLEBAR_INSET_LEADING_PROPERTY = '--mac-titlebar-inset-leading';

/**
 * Reads the inset from the URL and publishes it as a CSS custom property. Safe to call more than
 * once, and a no-op when the param is missing or unusable — a bad value must leave the bar at its
 * normal padding rather than throwing during boot.
 */
export function applyMacTitlebarInset(): void {
  try {
    const rawValue = new URLSearchParams(window.location.search).get(TITLEBAR_INSET_LEADING_PARAM);

    if (rawValue === null) return;

    const insetPoints = Number.parseFloat(rawValue);

    // Rejects NaN, negatives and 0 alike: all three mean "nothing to clear", and the fallback in
    // the CSS already handles that case.
    if (!Number.isFinite(insetPoints) || insetPoints <= 0) return;

    document.documentElement.style.setProperty(
      TITLEBAR_INSET_LEADING_PROPERTY,
      `${insetPoints}px`,
    );
  } catch {
    // Parsing the URL must never be able to block boot.
  }
}
